import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Check, X, Printer, Trash2, AlertTriangle, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldInput } from "./FieldInput";

const sel = "h-9 rounded-md border border-slate-300 bg-white text-sm px-2";

const EditRow = ({ row, fields, onSave, onCancel }) => {
  const [d, setD] = useState(row.data);
  const [status, setStatus] = useState(row.status);
  return (
    <tr className="bg-sky-50/60" data-testid={`row-edit-${row.row_id}`}>
      <td className="p-1 whitespace-nowrap">
        <Button size="icon" variant="ghost" onClick={() => onSave(d, status)} data-testid="row-edit-save"><Check className="w-4 h-4 text-emerald-600" /></Button>
        <Button size="icon" variant="ghost" onClick={onCancel} data-testid="row-edit-cancel"><X className="w-4 h-4" /></Button>
      </td>
      {fields.map((f) => <td key={f.key} className="p-1"><FieldInput compact field={f} value={d[f.key]} onChange={(v) => setD({ ...d, [f.key]: v })} testid={`row-edit-${f.key}`} /></td>)}
      <td className="p-1"><select className={`${sel} h-8 text-xs`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="row-edit-status">
        <option value="active">Active</option><option value="inactive">Inactive</option></select></td>
    </tr>
  );
};

export const DataTable = ({ template, rows, canEdit, canDelete, onSave, onDelete, onFilters }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dupOnly, setDupOnly] = useState(false);
  const [sort, setSort] = useState({ key: "", dir: 1 });
  const [editing, setEditing] = useState(null);
  const fields = template.fields;
  useEffect(() => { onFilters?.({ q: q.trim(), status, duplicates: dupOnly }); }, [q, status, dupOnly, onFilters]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = rows.filter((r) => (!status || r.status === status) && (!dupOnly || r.duplicate)
      && (!s || Object.values(r.data).some((v) => String(v).toLowerCase().includes(s))));
    if (sort.key) out = [...out].sort((a, b) => sort.dir * String(a.data[sort.key] || "").localeCompare(String(b.data[sort.key] || ""), undefined, { numeric: true }));
    return out;
  }, [rows, q, status, dupOnly, sort]);
  const dupCount = rows.filter((r) => r.duplicate).length;
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-slate-100">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search every column…" className="flex-1 min-w-[200px] bg-white" data-testid="data-search-input" />
        <select className={sel} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="data-filter-status">
          <option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option>
        </select>
        {dupCount > 0 && <Button size="sm" variant={dupOnly ? "default" : "outline"} onClick={() => setDupOnly(!dupOnly)} data-testid="data-filter-duplicates">
          <AlertTriangle className="w-3.5 h-3.5 mr-1" />{dupCount} possible duplicates</Button>}
        <span className="text-xs text-slate-500 ml-auto" data-testid="data-count">{shown.length} of {rows.length} entries</span>
      </div>
      <div className="max-h-[65vh] overflow-auto">
        <table className="w-full text-sm" data-testid="data-table">
          <thead className="sticky top-0 z-10 bg-slate-100 text-slate-600 text-xs">
            <tr>
              <th className="p-2 text-left w-[1%]"> </th>
              {fields.map((f) => (
                <th key={f.key} className="p-2 text-left whitespace-nowrap">
                  <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => setSort({ key: f.key, dir: sort.key === f.key ? -sort.dir : 1 })} data-testid={`data-sort-${f.key}`}>
                    {f.label}<ArrowUpDown className={`w-3 h-3 ${sort.key === f.key ? "text-amber-600" : "opacity-40"}`} />
                  </button>
                </th>
              ))}
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => editing === r.row_id ? (
              <EditRow key={r.row_id} row={r} fields={fields} onCancel={() => setEditing(null)}
                onSave={async (d, s) => { if (await onSave(r, d, s)) setEditing(null); }} />
            ) : (
              <tr key={r.row_id} className={`border-t border-slate-100 hover:bg-slate-50 ${r.status === "inactive" ? "opacity-60" : ""}`} data-testid={`data-row-${r.row_id}`}>
                <td className="p-1 whitespace-nowrap">
                  {canEdit && <Button size="icon" variant="ghost" onClick={() => setEditing(r.row_id)} data-testid={`row-edit-btn-${r.row_id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                  <Button size="icon" variant="ghost" asChild data-testid={`row-print-btn-${r.row_id}`}><Link to={`/forms/${template.template_id}/rows/${r.row_id}/print`}><Printer className="w-3.5 h-3.5" /></Link></Button>
                  {canDelete && <Button size="icon" variant="ghost" onClick={() => onDelete(r)} data-testid={`row-delete-btn-${r.row_id}`}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>}
                </td>
                {fields.map((f) => <td key={f.key} className="p-2 text-slate-800 max-w-[280px] truncate" title={r.data[f.key]}>{r.data[f.key] || <span className="text-slate-300">—</span>}</td>)}
                <td className="p-2 whitespace-nowrap">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{r.status}</span>
                  {r.duplicate && <AlertTriangle className="inline w-3.5 h-3.5 ml-1.5 text-amber-600" title="Possible duplicate" data-testid={`row-duplicate-${r.row_id}`} />}
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={fields.length + 2} className="p-10 text-center text-slate-500" data-testid="data-empty">No entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
